import {getCookie} from '@github-ui/cookies'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {FeaturePreviewDialog as ReactFeaturePreviewDialog} from '@github-ui/feature-preview-dialog'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {SSO_BANNER_COOKIE_NAME} from '@github-ui/global-sso-banner'
import {IncludeFragment} from '@github-ui/include-fragment-react'
import {
  type PropsWithPartialAnchor,
  type ReactPartialAnchorProps,
  useExternalAnchor,
} from '@github-ui/react-core/react-partial-anchor'
import {UnsafeHTMLDiv} from '@github-ui/safe-html/UnsafeHTML'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {testIdProps} from '@github-ui/test-id-props'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {type UserStatus, UserStatusDialog} from '@github-ui/user-status-dialog'
import {Emoji} from '@github-ui/user-status-dialog/Emoji'
import {
  AccessibilityIcon,
  AlertIcon,
  BeakerIcon,
  CodeSquareIcon,
  CopilotIcon,
  GearIcon,
  GlobeIcon,
  HeartIcon,
  type Icon,
  OrganizationIcon,
  PaintbrushIcon,
  PersonIcon,
  RepoIcon,
  ShieldIcon,
  SignOutIcon,
  SmileyIcon,
  StarIcon,
  UploadIcon,
} from '@primer/octicons-react'
import {
  ActionList,
  AnchoredOverlay,
  type AnchoredOverlayProps,
  IconButton,
  Label,
  Spinner,
  Stack,
  Tooltip,
  Truncate,
} from '@primer/react'
import {Dialog, type DialogHeaderProps, type DialogProps} from '@primer/react/experimental'
import {forwardRef, memo, type ReactNode, useCallback, useMemo, useRef, useState} from 'react'

import {AccountSwitcher, type AccountSwitcherProps} from './AccountSwitcher'
import {ErrorDialog, type ErrorDialogProps} from './ErrorDialog'
import drawerStyles from './GlobalUserNavDrawer.module.css'
import styles from './styles.module.css'
import {useEnterpriseTasksData} from './utils/use-enterprise-tasks-data'
import {type LazyLoadItemDataAttributes, useLazyData} from './utils/use-lazy-data'
import {useOrgTasksData} from './utils/use-org-tasks-data'

export function hasSSOBannerCookie() {
  const ssoCookie = getCookie(SSO_BANNER_COOKIE_NAME)
  return Boolean(ssoCookie?.value)
}

export interface GlobalUserNavDrawerProps extends Omit<AccountSwitcherProps, 'stashedAccounts' | 'setError'> {
  owner: {
    login: string
    name: string
    avatarUrl: string
  }
  lazyLoadItemDataFetchUrl: string
  showAccountSwitcher: boolean
  showCopilot: boolean
  showEnterprises: boolean
  showEnterprise: boolean
  showGists: boolean
  showOrganizations: boolean
  showSponsors: boolean
  showUpgrade: boolean
  showFeaturesPreviews: boolean
  showEnterpriseSettings: boolean
  gistsUrl: string
  yourEnterpriseUrl: string
  enterpriseSettingsUrl: string
}

type UserStatusItemProps = {
  lazyLoadItemData?: LazyLoadItemDataAttributes
  onClick: () => void
}

const defaultCategory = 'GLOBAL_NAV_USER_MENU'

const emptyAnalytics = {category: '', action: '', label: ''}

function NavLink({
  href,
  icon: NavIcon,
  children,
  analyticsAction,
  extraAnalytics = emptyAnalytics,
  extraOnClick,
}: {
  href: string
  icon: Icon
  extraAnalytics?: {
    category: string
    action: string
    label?: string
  }
  analyticsAction: string
  children: ReactNode

  /** click handler to fire in addition to the automatic analytics event built into NavLink */
  extraOnClick?: () => void
}) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const onClick = useCallback(() => {
    if (extraAnalytics.category) {
      sendClickAnalyticsEvent({
        category: extraAnalytics.category,
        action: extraAnalytics.action,
        label: extraAnalytics.label,
      })
    }

    sendClickAnalyticsEvent({
      category: defaultCategory,
      action: analyticsAction,
    })

    extraOnClick?.()
  }, [
    extraAnalytics.category,
    extraAnalytics.action,
    extraAnalytics.label,
    sendClickAnalyticsEvent,
    analyticsAction,
    extraOnClick,
  ])

  return (
    <ActionList.LinkItem href={href} onClick={onClick}>
      <ActionList.LeadingVisual>
        <NavIcon />
      </ActionList.LeadingVisual>
      {children}
    </ActionList.LinkItem>
  )
}

const UserStatusWrapper = ({children, hasStatusMessage}: {children: ReactNode; hasStatusMessage: boolean}) =>
  hasStatusMessage ? (
    <Tooltip direction="sw" delay="long" text="Edit status">
      {children}
    </Tooltip>
  ) : (
    children
  )

const UserStatusNavItem = memo(function UserStatusNavItem({lazyLoadItemData, onClick}: UserStatusItemProps) {
  // While in a loading state, explicitly label the button as "Set status" since it's still interactive
  const loadingAttributes = lazyLoadItemData ? undefined : {'aria-label': 'Set status', 'aria-labelledby': undefined}
  // check if the message is set to determine if a tooltip is needed
  const hasStatusMessage = (lazyLoadItemData?.userStatus?.messageHtml?.length ?? 0) > 0

  return (
    <UserStatusWrapper hasStatusMessage={hasStatusMessage}>
      <ActionList.Item {...testIdProps('global-user-nav-set-status-item')} {...loadingAttributes} onSelect={onClick}>
        <ActionList.LeadingVisual>
          {lazyLoadItemData?.userStatus?.emojiAttributes ? (
            <Emoji {...lazyLoadItemData?.userStatus.emojiAttributes} tone={lazyLoadItemData?.userPreferences?.tone} />
          ) : (
            <SmileyIcon />
          )}
        </ActionList.LeadingVisual>
        {lazyLoadItemData ? (
          <UnsafeHTMLDiv
            className={styles.emojiContainer}
            html={lazyLoadItemData.userStatus.messageHtml || 'Set status'}
          />
        ) : (
          <LoadingSkeleton height="md" />
        )}
      </ActionList.Item>
    </UserStatusWrapper>
  )
})

type UpgradeNavItemProps = {
  lazyLoadItemData?: LazyLoadItemDataAttributes
}

function UpgradeNavItem(props: UpgradeNavItemProps) {
  const enterpriseTrialUrl = props.lazyLoadItemData?.enterpriseTrialUrl
  if (enterpriseTrialUrl) {
    return (
      <NavLink
        href={enterpriseTrialUrl}
        icon={UploadIcon}
        extraAnalytics={{
          category: 'start_a_free_trial',
          action: 'click_to_set_up_enterprise_trial',
          label: 'ref_loc:side_panel;ref_cta:try_enterprise',
        }}
        analyticsAction="TRY_ENTERPRISE"
      >
        Try Enterprise
        {/* eslint-disable-next-line primer-react/direct-slot-children */}
        <ActionList.TrailingVisual>
          <Label variant="primary">Free</Label>
        </ActionList.TrailingVisual>
      </NavLink>
    )
  } else {
    return (
      <NavLink href="/account/choose?action=upgrade" icon={UploadIcon} analyticsAction="UPGRADE_PLAN">
        Upgrade
      </NavLink>
    )
  }
}

const FeaturePreviewDialog = forwardRef<HTMLDivElement, {onClose: () => void; login: string}>(
  ({onClose, login}, ref) => {
    return (
      <Dialog
        ref={ref}
        title="Feature preview dialog"
        onClose={onClose}
        renderBody={() => {
          return (
            <Dialog.Body className="p-0">
              <IncludeFragment src={`/users/${login}/feature_previews`}>
                <p className="text-center mt-3" data-hide-on-error>
                  <Spinner />
                </p>
                <p className="flash flash-error mb-0 mt-2" data-show-on-error hidden>
                  <AlertIcon />
                  Sorry, something went wrong and we were not able to fetch the feature previews
                </p>
              </IncludeFragment>
            </Dialog.Body>
          )
        }}
        className={drawerStyles.Dialog}
      />
    )
  },
)
FeaturePreviewDialog.displayName = 'FeaturePreviewDialog'

/* This component will eventually use the Dialog component from Primer React to encapsulate the entire global user
 * nav drawer. We aren't able to manage the dialog here because the user nav drawer opens several constituent dialogs -
 * the user status dialog and the feature preview dialog - which are currently rendered in Rails-land using the Primer
 * Dialog component from primer_view_components. It's architected this way because dialogs from the two frameworks
 * have proven to be somewhat incompatible. For example, pressing 'esc' closes the nav drawer instead of the user
 * status dialog, etc. Keeping all the actual dialogs in Rails sidesteps these issues. When the constituent dialogs
 * are eventually ported to React, we can stick a Dialog in here and manage the entire nav drawer in React.
 *
 * We've actually tried this already. See
 * https://github.com/github/github-ui/blob/999d5af6e063bbce5e676158d2c076d95cd2a565/packages/global-user-nav-drawer/GlobalUserNavDrawer.tsx
 * for inspiration when it's time to move the dialog into this component.
 */
function GlobalUserNavDrawerDialog(
  props: GlobalUserNavDrawerProps & {
    onClose: DialogProps['onClose']

    /** Reference to the anchor element for the dialog */
    anchorRef: React.RefObject<HTMLElement | null>
  },
) {
  const {data: lazyLoadItemData, updateUserStatus} = useLazyData(props.lazyLoadItemDataFetchUrl)
  const {data: lazyOrganizationNewTasksData} = useOrgTasksData()
  const {data: lazyEnterpriseNewTasksData} = useEnterpriseTasksData()

  const [showUserStatusDialog, setShowUserStatusDialog] = useState(false)
  const [showFeaturePreviewDialog, setShowFeaturePreviewDialog] = useState(false)
  const {onClose: onCloseBase, owner} = props
  const profilePath = `/${owner.login}`
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const onClose = useCallback<DialogProps['onClose']>(
    gesture => {
      onCloseBase(gesture)
    },
    [onCloseBase],
  )

  const openUserStatusDialog = useCallback(() => {
    setShowUserStatusDialog(true)
    sendClickAnalyticsEvent({category: defaultCategory, action: 'USER_STATUS'})
  }, [sendClickAnalyticsEvent])

  const onUserStatusClosed = useCallback(
    async (statusPromise?: Promise<UserStatus> | string) => {
      setShowUserStatusDialog(false)

      if (statusPromise && typeof statusPromise !== 'string') {
        try {
          updateUserStatus(await statusPromise)
        } catch {
          // Do nothing
        }
      }
    },
    [updateUserStatus],
  )

  const openFeaturePreviewDialog = useCallback(() => {
    setShowFeaturePreviewDialog(true)
    sendClickAnalyticsEvent({category: defaultCategory, action: 'FEATURE_PREVIEW'})
  }, [sendClickAnalyticsEvent])

  const renderHeader = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    ({dialogLabelId}: DialogHeaderProps) => {
      return (
        <div
          className="d-flex pr-3 pl-3 pt-3"
          id={dialogLabelId}
          aria-label="User navigation"
          role="heading"
          aria-level={1}
        >
          <div className="d-flex flex-1">
            <div className="d-flex">
              <GitHubAvatar src={owner.avatarUrl} size={32} />
              <div className="lh-condensed overflow-hidden d-flex flex-column flex-justify-center ml-2 f5 mr-auto">
                <div className="text-bold">
                  <Truncate title={owner.login} maxWidth={120}>
                    {owner.login}
                  </Truncate>
                </div>
                <div className="fgColor-muted">
                  <Truncate title={owner.name} maxWidth={120}>
                    {owner.name}
                  </Truncate>
                </div>
              </div>
            </div>
          </div>
          {props.showAccountSwitcher && (
            <AccountSwitcher
              canAddAccount={props.canAddAccount}
              addAccountPath={props.addAccountPath}
              switchAccountPath={props.switchAccountPath}
              stashedAccounts={lazyLoadItemData?.stashedAccounts ?? null}
              loginAccountPath={props.loginAccountPath}
              setError={setError}
            />
          )}
          <div className="d-md-none">
            <Dialog.CloseButton onClose={() => onClose('close-button')} />
          </div>
        </div>
      )
    },
    [
      onClose,
      owner,
      lazyLoadItemData?.stashedAccounts,
      props.canAddAccount,
      props.addAccountPath,
      props.switchAccountPath,
      props.loginAccountPath,
      props.showAccountSwitcher,
    ],
  )

  const [error, setError] = useState<ErrorDialogProps | false>(false)

  // See https://github.com/primer/react/issues/6563
  // Interacting with a nested `Dialog` component inside of an AnchoredOverlay causes both to be dismissed.
  // Track the refs for the launched dialogs and force ignore any interactions for their overlays
  const featurePreviewRef = useRef<HTMLDivElement>(null)
  const userStatusRef = useRef<HTMLDivElement>(null)
  const ignoreClickRefs = useMemo(() => [featurePreviewRef, userStatusRef], [featurePreviewRef, userStatusRef])

  if (error) {
    return <ErrorDialog {...error} onClose={() => setError(false)} />
  }

  const FeaturePreviewComp = isFeatureEnabled('global_nav_react_feature_preview')
    ? ReactFeaturePreviewDialog
    : FeaturePreviewDialog

  return (
    <ReductiveUserNavDrawer
      onClose={reason => {
        if (isFeatureEnabled('global_nav_react_feature_preview') && showFeaturePreviewDialog) {
          // If the feature preview dialog is open, don't close the entire nav drawer
          return
        }

        onClose(reason)
      }}
      renderHeader={renderHeader}
      anchorRef={props.anchorRef}
      ignoreClickRefs={ignoreClickRefs}
    >
      {showUserStatusDialog && (
        <UserStatusDialog
          userStatusData={lazyLoadItemData?.userStatus}
          userPreferences={lazyLoadItemData?.userPreferences}
          ref={userStatusRef}
          onClose={onUserStatusClosed}
        />
      )}
      {showFeaturePreviewDialog && (
        <FeaturePreviewComp
          ref={featurePreviewRef}
          onClose={() => setShowFeaturePreviewDialog(false)}
          login={props.owner.login}
        />
      )}
      <Stack gap="condensed">
        <ActionList variant="full">
          <UserStatusNavItem lazyLoadItemData={lazyLoadItemData} onClick={openUserStatusDialog} />
          <SSONavLink lazyLoadItemData={lazyLoadItemData} />
          <ActionList.Divider />

          <NavLink href={profilePath} icon={PersonIcon} analyticsAction="PROFILE">
            Profile
          </NavLink>
          <NavLink href={`${profilePath}?tab=repositories`} icon={RepoIcon} analyticsAction="YOUR_REPOSITORIES">
            Repositories
          </NavLink>
          <NavLink href={`${profilePath}?tab=stars`} icon={StarIcon} analyticsAction="YOUR_STARS">
            Stars
          </NavLink>

          {props.showGists && (
            <NavLink href={props.gistsUrl} icon={CodeSquareIcon} analyticsAction="YOUR_GISTS">
              Gists
            </NavLink>
          )}
          {props.showOrganizations && (
            <NavLink
              href="/settings/organizations"
              icon={OrganizationIcon}
              analyticsAction="YOUR_ORGANIZATIONS"
              extraOnClick={() => {
                // Piggy back onto the NavLinks onClick, so that clicking either the visual button or just clicking the
                // NavLink normally will result in a Nudge analytics event in addition to the standard Nav analytics event
                if (lazyOrganizationNewTasksData?.enableOrganizationNewTasksExperiment) {
                  sendClickAnalyticsEvent({
                    location: 'global_user_nav_drawer',
                    category: lazyOrganizationNewTasksData.showNewTasksButton
                      ? 'nav_organizations_new_tasks_nudge_1'
                      : 'nav_organizations_new_tasks_nudge_0',
                    action: 'new_task',
                    tag: 'a',
                    group: 'engage',
                  })
                }
              }}
            >
              Organizations
              {lazyOrganizationNewTasksData?.enableOrganizationNewTasksExperiment &&
                (lazyOrganizationNewTasksData.showNewTasksButton ? (
                  /* eslint-disable-next-line primer-react/direct-slot-children */
                  <ActionList.TrailingVisual>
                    <span data-analytics-visible='{"category":"nav_organizations_new_tasks_nudge_1","action":"visible","group":"engage"}' />
                    <Label variant="done">New task</Label>
                  </ActionList.TrailingVisual>
                ) : (
                  <span data-analytics-visible='{"category":"nav_organizations_new_tasks_nudge_0","action":"visible","group":"engage"}' />
                ))}
            </NavLink>
          )}
          {props.showEnterprises && (
            <NavLink
              href="/settings/enterprises"
              icon={GlobeIcon}
              extraAnalytics={{
                category: 'enterprises_more_discoverable',
                action: 'click_your_enterprises',
                label: 'ref_loc:side_panel;ref_cta:your_enterprises;is_navigation_redesign:true',
              }}
              analyticsAction="YOUR_ENTERPRISES"
              extraOnClick={() => {
                // Piggy back onto the NavLinks onClick, so that clicking either the visual button or just clicking the
                // NavLink normally will result in a Nudge analytics event in addition to the standard Nav analytics event
                if (lazyEnterpriseNewTasksData?.enableEnterpriseNewTasksExperiment) {
                  sendClickAnalyticsEvent({
                    location: 'global_user_nav_drawer',
                    category: lazyEnterpriseNewTasksData.showNewTasksButton
                      ? 'nav_enterprises_new_tasks_nudge_1'
                      : 'nav_enterprises_new_tasks_nudge_0',
                    action: 'new_task',
                    tag: 'a',
                    group: 'engage',
                  })
                  // send our old analytics category which we used prior to October 2025
                  sendClickAnalyticsEvent({
                    location: 'global_user_nav_drawer',
                    category: 'dfd_nav_new_tasks_nudge_1',
                    action: 'new_task',
                    tag: 'a',
                    group: 'engage',
                  })
                }
              }}
            >
              Enterprises
              {lazyEnterpriseNewTasksData?.enableEnterpriseNewTasksExperiment &&
                (lazyEnterpriseNewTasksData.showNewTasksButton ? (
                  /* eslint-disable-next-line primer-react/direct-slot-children */
                  <ActionList.TrailingVisual>
                    <span data-analytics-visible='{"category":"dfd_nav_new_tasks_nudge_1","action":"visible","group":"engage"}' />
                    <span data-analytics-visible='{"category":"nav_enterprises_new_tasks_nudge_1","action":"visible","group":"engage"}' />
                    <Label variant="done">New task</Label>
                  </ActionList.TrailingVisual>
                ) : (
                  <span data-analytics-visible='{"category":"nav_enterprises_new_tasks_nudge_0","action":"visible","group":"engage"}' />
                ))}
            </NavLink>
          )}
          {props.showEnterprise && (
            <NavLink href={props.yourEnterpriseUrl} icon={GlobeIcon} analyticsAction="YOUR_ENTERPRISE">
              Enterprise
            </NavLink>
          )}
          {props.showSponsors && (
            <NavLink href="/sponsors/accounts" icon={HeartIcon} analyticsAction="SPONSORS">
              Sponsors
            </NavLink>
          )}

          <ActionList.Divider />

          <NavLink href="/settings/profile" icon={GearIcon} analyticsAction="SETTINGS">
            Settings
          </NavLink>

          {props.showEnterpriseSettings && (
            <NavLink href={props.enterpriseSettingsUrl} icon={GlobeIcon} analyticsAction="ENTERPRISE_SETTINGS">
              Enterprise settings
            </NavLink>
          )}

          {props.showCopilot && (
            <NavLink
              href="/settings/copilot"
              icon={CopilotIcon}
              extraAnalytics={{
                category: 'try_copilot',
                action: 'click_to_try_copilot',
                label: 'ref_loc:side_panel;ref_cta:your_copilot',
              }}
              analyticsAction="COPILOT_SETTINGS"
            >
              Copilot settings
            </NavLink>
          )}

          {props.showFeaturesPreviews && (
            <ActionList.Item onSelect={openFeaturePreviewDialog}>
              <ActionList.LeadingVisual>
                <BeakerIcon />
              </ActionList.LeadingVisual>
              {lazyLoadItemData?.hasUnseenFeatures && (
                <ActionList.TrailingVisual>
                  <Label variant="accent">New</Label>
                </ActionList.TrailingVisual>
              )}
              <span>Feature preview</span>
            </ActionList.Item>
          )}

          <NavLink href="/settings/appearance" icon={PaintbrushIcon} analyticsAction="APPEARANCE_SETTINGS">
            Appearance
          </NavLink>
          <NavLink href="/settings/accessibility" icon={AccessibilityIcon} analyticsAction="ACCESSIBILITY_SETTINGS">
            Accessibility
          </NavLink>

          {props.showUpgrade && <UpgradeNavItem lazyLoadItemData={lazyLoadItemData} />}

          <ActionList.Divider />

          <NavLink href="/logout" icon={SignOutIcon} analyticsAction="LOGOUT">
            Sign out
          </NavLink>
        </ActionList>
      </Stack>
    </ReductiveUserNavDrawer>
  )
}

function ExternallyAnchoredGlobalUserNavDrawer(
  props: PropsWithPartialAnchor<Omit<GlobalUserNavDrawerProps, 'anchorRef'>>,
) {
  const {open, setOpen, ref: anchorRef} = useExternalAnchor(props.reactPartialAnchor)
  const onClose = useCallback(() => {
    setOpen(false)
    setTimeout(() => {
      // Dialog will soon support `returnFocusRef`, which should be used instead
      anchorRef.current?.focus()
    })
  }, [setOpen, anchorRef])

  if (open) {
    return <GlobalUserNavDrawerDialog {...props} anchorRef={anchorRef} onClose={onClose} />
  }

  return <></>
}

export function InternallyAnchoredGlobalUserNavDrawer(
  props: GlobalUserNavDrawerProps & {
    initialOpen?: boolean
  },
) {
  const [open, setOpen] = useState(props.initialOpen ?? false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const label = `${open ? 'Close' : 'Open'} user navigation menu`
  return (
    <>
      <IconButton
        ref={anchorRef}
        className={drawerStyles.anchor}
        variant="invisible"
        icon={() => <GitHubAvatar size={32} alt={props.owner.name ?? 'User avatar'} src={props.owner?.avatarUrl} />}
        aria-label={label}
        aria-haspopup="menu"
        onClick={() => {
          setOpen(!open)
          sendClickAnalyticsEvent({category: defaultCategory, action: 'USER_NAV_MENU_TOGGLE', label})
        }}
        data-login={props.owner.login}
        {...testIdProps('global-user-nav-drawer-toggle')}
      />

      {open && <GlobalUserNavDrawerDialog {...props} anchorRef={anchorRef} onClose={() => setOpen(false)} />}
    </>
  )
}

export function GlobalUserNavDrawer(props: GlobalUserNavDrawerProps & Partial<ReactPartialAnchorProps>) {
  if (props.reactPartialAnchor) {
    return <ExternallyAnchoredGlobalUserNavDrawer {...props} reactPartialAnchor={props.reactPartialAnchor} />
  }

  // If no anchor is provided, assume the drawer state is externally controlled
  return <InternallyAnchoredGlobalUserNavDrawer {...props} />
}

export function SSONavLink({lazyLoadItemData}: {lazyLoadItemData?: LazyLoadItemDataAttributes}) {
  // If we haven't loaded the SSO data yet, fallback to checking for a cookie to be optimistic
  // Since the cookie only is set if they dismiss the SSO banner, we can use it as a proxy for the user belonging to at least one SSO org

  if ((!lazyLoadItemData && hasSSOBannerCookie()) || lazyLoadItemData?.showSSO) {
    return (
      <NavLink href={lazyLoadItemData?.ssoUrl ?? '/settings/organizations'} icon={ShieldIcon} analyticsAction="SSO">
        Single sign-on
      </NavLink>
    )
  }

  return null
}

function ReductiveUserNavDrawer(
  props: React.PropsWithChildren<{
    onClose: DialogProps['onClose']
    renderHeader: (props: React.PropsWithChildren<DialogHeaderProps>) => React.ReactElement
    ignoreClickRefs?: Array<React.RefObject<HTMLElement | null>>
    anchorRef: React.RefObject<HTMLElement | null>
  }>,
) {
  const {onClose: onCloseDefault, anchorRef} = props

  const onClose = useCallback(
    (gesture: 'anchor-click' | 'click-outside' | 'escape' | 'close') => {
      if (gesture === 'anchor-click' || gesture === 'click-outside' || gesture === 'close') {
        onCloseDefault('close-button')
        return
      }

      onCloseDefault('escape')
    },
    [onCloseDefault],
  )

  const overlayProps = useMemo<AnchoredOverlayProps['overlayProps']>(() => {
    return {
      ignoreClickRefs: [anchorRef, ...(props.ignoreClickRefs ?? [])],
      overflow: 'auto',
      maxHeight: 'fit-content',
      role: 'dialog',
      'aria-labelledby': 'global-user-nav-drawer-header',
      'aria-describedby': 'global-user-nav-drawer-description',
    }
  }, [anchorRef, props.ignoreClickRefs])

  return (
    <AnchoredOverlay
      open
      onClose={onClose}
      renderAnchor={null}
      anchorRef={anchorRef}
      displayCloseButton={false}
      width="small"
      variant={{
        narrow: 'fullscreen',
        regular: 'anchored',
      }}
      focusZoneSettings={{
        activeDescendantFocus: true,
      }}
      overlayProps={overlayProps}
    >
      {props.renderHeader?.({
        dialogLabelId: 'global-user-nav-drawer-header',
        dialogDescriptionId: 'global-user-nav-drawer-description',
        onClose: onCloseDefault,
      })}
      <div className="p-2">{props.children}</div>
    </AnchoredOverlay>
  )
}

try{ NavLink.displayName ||= 'NavLink' } catch {}
try{ UserStatusWrapper.displayName ||= 'UserStatusWrapper' } catch {}
try{ UserStatusNavItem.displayName ||= 'UserStatusNavItem' } catch {}
try{ UpgradeNavItem.displayName ||= 'UpgradeNavItem' } catch {}
try{ GlobalUserNavDrawerDialog.displayName ||= 'GlobalUserNavDrawerDialog' } catch {}
try{ FeaturePreviewComp.displayName ||= 'FeaturePreviewComp' } catch {}
try{ ExternallyAnchoredGlobalUserNavDrawer.displayName ||= 'ExternallyAnchoredGlobalUserNavDrawer' } catch {}
try{ InternallyAnchoredGlobalUserNavDrawer.displayName ||= 'InternallyAnchoredGlobalUserNavDrawer' } catch {}
try{ GlobalUserNavDrawer.displayName ||= 'GlobalUserNavDrawer' } catch {}
try{ SSONavLink.displayName ||= 'SSONavLink' } catch {}
try{ ReductiveUserNavDrawer.displayName ||= 'ReductiveUserNavDrawer' } catch {}